import { Select as BaseSelect } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import store from 'stores/RootStore';

const { Option } = BaseSelect;

const Container = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const Label = styled.span`
  font-weight: bold;
  width: 150px;
`;

const Select = styled(BaseSelect)`
  flex: 1;
`;

const CLEAR_ALL_VALUE = '\x1eclear-all';
const SELECT_ALL_VALUE = '\x1eselect-all';

function isStringArray(x: unknown): x is string[] {
  return Array.isArray(x) && x.every((el) => typeof el === 'string');
}

/**
 * The identity a property presents to the Select: out as an Option value, and
 * back through onChange.
 *
 * A key is needed rather than the object itself for two stacked reasons. antd's
 * value channel carries strings, and the constraints held in `current` are
 * fresh objects rather than the options they were built from, so there is no
 * identity to appeal to.
 *
 * Qualifying by type is what makes it injective: within one (name, type) the
 * values are `===`-distinct by FilterManager.available's own rule, and String
 * does not collapse distinct primitives of a single type. Without the type,
 * `NUMBER 1` and `TEXT '1'` would present as the same option.
 */
function stringKey(property: IDynamicProperty<any>) {
  return `\x1f${property.type}\x1f${property.value}`;
}

/**
 * Group the possible values by the key they present to the Select
 * component.
 *
 * This uses buckets rather than a direct value mapping as a defensive
 * measure - I'm not sure exactly how likely this is but we can make noise
 * (in the form of console.warn) if it ever happens.
 */
function groupPropertiesByKey(
  properties: ReadonlyArray<IDynamicProperty<any>>
) {
  const byKey = new Map<string, IDynamicProperty<any>[]>();

  for (const property of properties) {
    const key = stringKey(property);
    const bucket = byKey.get(key);

    if (bucket) {
      bucket.push(property);
    } else {
      byKey.set(key, [property]);
    }
  }

  return byKey;
}

export interface DynamicPropertyDropdownProps {
  /**
   * The property name every option shares. The caller groups `all` by name to
   * build `values`, so it already holds this; taking it as a prop saves
   * recovering it from an arbitrary element, and means the row can still label
   * itself when `values` comes back empty.
   */
  name: string;
  /**
   * Every distinct value seen for `name` across the creative's ads - one
   * dropdown choice each, not one match. FilterManager.all admits a property
   * only when (name, type, value) is new, so a name with three values appears
   * three times.
   */
  values: ReadonlyArray<IDynamicProperty<any>>;
}

function DynamicPropertyDropdown({
  name,
  values: possibleValues,
}: DynamicPropertyDropdownProps) {
  const filterManager = store.creative?.ads.filter;

  const activeConstraintsForThisName =
    filterManager?.current.properties.filter(
      (constraint) => constraint.name === name
    ) ?? [];

  const valuesByKey = groupPropertiesByKey(possibleValues);

  for (const bucket of valuesByKey.values()) {
    if (bucket.length > 1) {
      const logName = JSON.stringify(name);
      const typeList = bucket.map((property) => property.type).join(', ');
      console.warn(
        `[DynamicPropertyDropdown] Property ${logName} has` +
          ` ${bucket.length} values with non-unique keys` +
          ` (types: ${typeList}).` +
          ` They cannot be distinguished.`
      );
    }
  }

  const handleOnSelect = (selection: unknown) => {
    if (!filterManager || !isStringArray(selection)) return;

    if (selection.includes(SELECT_ALL_VALUE)) {
      filterManager.setConstraintsForName(name, possibleValues);
    } else if (selection.includes(CLEAR_ALL_VALUE)) {
      filterManager.setConstraintsForName(name, []);
    } else {
      // Urecognized keys fall away, which is how free text typed into the
      // tags input gets ignored.
      const constraints = selection.flatMap(
        (key) => valuesByKey.get(key) ?? []
      );
      filterManager.setConstraintsForName(name, constraints);
    }
  };

  const canSelect = filterManager && possibleValues.length > 0;

  return (
    <Container>
      <Label>{name}</Label>

      <Select
        disabled={!canSelect}
        mode="tags"
        onChange={handleOnSelect}
        // Required, not decorative: the default search matches on `value`, and
        // ours is the encoded stringKey. Searching that would match the type
        // name embedded in it, and the separators are invisible to type.
        optionFilterProp="label"
        tokenSeparators={[',']}
        value={activeConstraintsForThisName.map((p) => stringKey(p))}
      >
        <Option key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
          Select All
        </Option>

        <Option key={CLEAR_ALL_VALUE} value={CLEAR_ALL_VALUE}>
          Clear All
        </Option>

        {[...valuesByKey].map(([key, bucket]) => (
          <Option key={key} label={bucket[0].label} value={key}>
            {bucket[0].label}
          </Option>
        ))}
      </Select>
    </Container>
  );
}

export default observer(DynamicPropertyDropdown);
